import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { chapterDetailMap, chapterList, chapters } from "@/mocks/mangaData";

export default function ChapterDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const detail = chapterDetailMap[id];
  const meta = chapters.find((c) => c.id === id);
  const current = chapterList.find((c) => c.id === id);

  usePageMeta({
    title: detail && current
      ? `${current.num} ${current.title} | 分话详解 - 甜蜜惩罚漫画剧情追踪站`
      : "分话解析 - 甜蜜惩罚漫画剧情追踪站",
    description:
      detail && current
        ? `${current.num}《${current.title}》分话详解：剧情导读、人物反应拆解与伏笔回收信号，帮助理解甜蜜惩罚漫画剧情推进。`
        : "甜蜜惩罚漫画分话解析，逐话拆解关键节点与伏笔回收。",
    jsonLd: detail && current
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${current.num} ${current.title} 分话详解`,
          description: current.summary,
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: "甜蜜惩罚漫画剧情追踪站" },
          publisher: { "@type": "Organization", name: "甜蜜惩罚漫画剧情追踪站" },
          mainEntityOfPage: `/chapters/${id}`,
        }
      : undefined,
  });

  if (!detail || !current) {
    return (
      <Layout>
        <PageHeader
          title="分话不存在"
          subtitle="没有找到对应的分话解析，可能内容还在补全中。"
          crumbs={[{ label: "首页", path: "/" }, { label: "分话解析", path: "/chapters" }, { label: "未找到" }]}
        />
        <section className="py-16 text-center">
          <Link
            to="/chapters"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 text-background-50 px-6 py-3 text-sm font-medium hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line text-base leading-none"></i>
            返回分话列表
          </Link>
        </section>
      </Layout>
    );
  }

  const image = chapters.find((c) => c.id === id)?.image;
  const related = chapterList.filter((c) => c.id !== id).slice(0, 3);

  return (
    <Layout>
      <PageHeader
        title={`${current.num} · ${current.title}`}
        subtitle={detail.intro}
        crumbs={[
          { label: "首页", path: "/" },
          { label: "分话解析", path: "/chapters" },
          { label: current.num },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          {image && (
            <div className="mb-8 overflow-hidden rounded-lg border border-background-200">
              <img
                src={image}
                alt={`${current.num} ${current.title} 分话插画`}
                className="h-56 md:h-72 w-full object-cover"
              />
            </div>
          )}

          {/* 重点速览 */}
          <div className="mb-8 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <h2 className="font-heading text-base md:text-lg font-semibold text-accent-900 mb-3">
              <i className="ri-star-line mr-2"></i>
              本话重点速览
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {detail.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line text-accent-500"></i>
                  {h}
                </li>
              ))}
            </ul>
          </div>

          {/* 正文分段 */}
          {detail.sections.map((sec, i) => (
            <article key={sec.heading} className="mb-8">
              <h2 className="flex items-center gap-3 font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-primary-500 text-background-50 text-sm font-bold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {sec.heading}
              </h2>
              <div className="space-y-3">
                {sec.body.map((p, pi) => (
                  <p key={pi} className="text-sm md:text-[15px] leading-7 text-foreground-800">
                    {p}
                  </p>
                ))}
              </div>
            </article>
          ))}

          <p className="rounded-md bg-background-100 text-xs leading-6 text-foreground-600 px-4 py-3">
            本文为读者向剧情梳理，基于已公开分话整理，仅供参考，具体剧情以官方更新内容为准。
          </p>

          {/* 相关推荐 */}
          <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-lg font-semibold mb-4">相关分话推荐</h2>
            <ul className="flex flex-col gap-3">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/chapters/${r.id}`}
                    className="group flex items-center justify-between rounded-md bg-background-50 border border-background-200 px-4 py-3 hover:border-primary-300 transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <span className="font-heading text-sm font-bold text-primary-600 whitespace-nowrap">
                        {r.num}
                      </span>
                      <span className="text-sm font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                        {r.title}
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600"></i>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Link
              to="/chapters"
              className="inline-flex items-center gap-1.5 rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-left-line text-base leading-none"></i>
              返回分话列表
            </Link>
            <Link
              to="/plot"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              查看剧情主线
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}